import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import Modal, { useModal } from '../Modal';
import styles from '../../styles/DownloadDataModal.module.scss';
import Icon from '../Icon';
import downloadData from '../../utils/download-data';
import { MetricsDataWithExternalTrips } from '../metrics/use-metrics';
import { DateTime } from 'luxon';
import {
    api,
    ExternalTripCountsData,
    NoDataError,
    VehicleClass,
} from '../../data/api';
import {
    LinearUnitsInfo,
    useLinearUnitsInfo,
} from '../../utils/use-linear-units';
import { convertLinearUnitsForDisplay } from '../../utils/units-conversion';
import {
    getEarliestDataDateIncludingExternalTripsFromString,
    getExternalCountForPeriod,
} from '../../utils/external-trip-counts';

const getAllMetricsData = async (
    slug: string,
    vehicleClass: string,
    earliestAvailable: string,
    externalTripCounts: ExternalTripCountsData | null
): Promise<Record<string, MetricsDataWithExternalTrips>> => {
    const data: Record<string, MetricsDataWithExternalTrips> = {};

    // add the data for all time
    try {
        const metrics = (await api.metrics(
            slug,
            vehicleClass as VehicleClass,
            'all'
        )) as MetricsDataWithExternalTrips;
        if (externalTripCounts) {
            const externalTripCount = getExternalCountForPeriod(
                'all',
                vehicleClass as VehicleClass,
                externalTripCounts.tripCounts
            );
            metrics.externalTripCount =
                Math.round(externalTripCount / 100) * 100;
        }
        data['all'] = metrics;
    } catch (err) {
        if (!(err instanceof NoDataError)) {
            throw err;
        }
    }

    // go through every quarter and get data
    let currentDateTime = externalTripCounts
        ? getEarliestDataDateIncludingExternalTripsFromString(
              earliestAvailable,
              externalTripCounts
          )
        : DateTime.fromISO(earliestAvailable);

    while (
        currentDateTime.endOf('quarter').toMillis() < DateTime.now().toMillis()
    ) {
        // get a string for the time range, like 2021-Q3
        const currentRange = `${currentDateTime.year}-Q${currentDateTime.quarter}`;

        try {
            const metrics = (await api.metrics(
                slug,
                vehicleClass as VehicleClass,
                currentRange
            )) as MetricsDataWithExternalTrips;
            if (externalTripCounts) {
                const externalTripCount = getExternalCountForPeriod(
                    currentRange,
                    vehicleClass as VehicleClass,
                    externalTripCounts.tripCounts
                );
                // Round for export
                metrics.externalTripCount =
                    Math.round(externalTripCount / 100) * 100;
            }
            data[currentRange] = metrics;
        } catch (err) {
            if (!(err instanceof NoDataError)) {
                throw err;
            }
            if (externalTripCounts) {
                const externalTripCount = getExternalCountForPeriod(
                    currentRange,
                    vehicleClass as VehicleClass,
                    externalTripCounts?.tripCounts
                );
                // This is a bit smelly but we want to return the expected data structure with only external trip counts
                // This approach is only used for data download
                const emptyMetricsWithExternalTrips = getEmptyMetricsData();
                emptyMetricsWithExternalTrips.externalTripCount =
                    Math.round(externalTripCount / 100) * 100;
                data[currentRange] = emptyMetricsWithExternalTrips;
            }
        }

        // increment our DateTime by 1 quarter
        currentDateTime = currentDateTime.plus({ quarter: 1 });
    }

    return data;
};

function getEmptyMetricsData() {
    return {
        totalTripCount: 0,
        averageTripsPerDay: 0,
        averageTripSpeedMetersPerSecond: 0,
        tripDuration: {
            average: 0,
            median: 0,
            xUnit: 'miles',
            yUnit: 'miles',
            dataBuckets: [],
        },
        tripsPerDay: {
            average: 0,
            median: 0,
            xUnit: 'miles',
            yUnit: 'miles',
            dataBuckets: [],
        },
        tripDistancePerDay: {
            average: 0,
            median: 0,
            xUnit: 'miles',
            yUnit: 'miles',
            dataBuckets: [],
            sumTotal: 0,
        },
        meanActiveVehiclesPerDay: {
            average: 0,
            median: 0,
            xUnit: 'miles',
            yUnit: 'miles',
            dataBuckets: [],
        },
        tripsPerVehiclePerDay: {
            average: 0,
            median: 0,
            xUnit: 'miles',
            yUnit: 'miles',
            dataBuckets: [],
        },
    } as MetricsDataWithExternalTrips;
}

function getTotalForRow(
    metrics: MetricsDataWithExternalTrips,
    externalTripCounts: ExternalTripCountsData | null
) {
    if (!externalTripCounts) {
        return null;
    }
    return metrics.externalTripCount
        ? (metrics.totalTripCount + metrics.externalTripCount).toFixed(0)
        : metrics.totalTripCount;
}

/**
 * Prepares a CSV data string for downloading Metrics data. Handles adding
 * headers, data, and the proper data format type.
 */
const generateMetricsCSV = async (
    slug: string,
    vehicleClass: string,
    earliestAvailable: string,
    linearUnits: LinearUnitsInfo,
    externalTripCounts: ExternalTripCountsData | null
) => {
    const metricsByTimePeriod = await getAllMetricsData(
        slug,
        vehicleClass,
        earliestAvailable,
        externalTripCounts
    );

    const headerRow = [
        'Time Period',
        `Median Trip Distance (${linearUnits.units})`,
        'Median Trip Duration (minutes)',
        `Average Trip Distance (${linearUnits.units})`,
        'Average Trip Duration (minutes)',
        'Average Trips per Day',
        `Total Distance (${linearUnits.units})`,
        'Total MDS Trips',
    ];

    if (externalTripCounts) {
        headerRow.push('Total Non-MDS Trips*');
        headerRow.push('Total Combined Trips');
        // Include external trips metadata in header to avoid making first column super wide
        headerRow.push(
            '"*' + externalTripCounts.external_trips_explanation + '"'
        );
    }

    const dataRows = Object.entries(metricsByTimePeriod).map(
        ([timeRange, metrics]) => {
            const metricsUnits = metrics.tripDistancePerDay.yUnit; // Linear distance is on the x-axis in this case
            return [
                timeRange,
                convertLinearUnitsForDisplay(
                    metrics.tripDistancePerDay.median,
                    metricsUnits,
                    linearUnits
                ).toFixed(2),
                metrics.tripDuration.median.toFixed(1),
                convertLinearUnitsForDisplay(
                    metrics.tripDistancePerDay.average,
                    metricsUnits,
                    linearUnits
                ).toFixed(2),
                metrics.tripDuration.average.toFixed(1),
                metrics.averageTripsPerDay.toFixed(0),
                convertLinearUnitsForDisplay(
                    metrics.tripDistancePerDay.sumTotal,
                    metricsUnits,
                    linearUnits
                ).toFixed(0),
                metrics.totalTripCount.toFixed(0),
                externalTripCounts ? metrics.externalTripCount : null,
                getTotalForRow(metrics, externalTripCounts),
            ];
        }
    );

    return (
        // add the header
        headerRow.join(',') +
        '\n' +
        // add our data rows
        dataRows.map(row => row.join(',')).join('\n')
    );
};

/**
 * Prepares a CSV data string for Routes data. Handles adding headers, data,
 * and the proper data format type.
 */
const generateRoutesCSV = routes => {
    const headerRow = [
        'Segment Name',
        'Percentage of Matched Trips',
        'Count of Matched Trips',
        'Segment Geometry ID',
    ];

    const { _data: data } = routes;
    // we can't make a CSV if there's no FeatureCollection
    if (data == null || typeof data === 'string' || data.type === 'Feature') {
        return;
    }

    const dataRows = data.features
        .sort((a, b) => b.properties?.percentage - a.properties?.percentage) // highest to lowest
        .map(f => [
            f.properties?.name,
            // floats may be stored as 8.00000000000000001 instead of 8.0
            f.properties?.percentage.toFixed(1),
            // must wrap strings in quotes so the commas don't bork the CSV format
            `"${f.properties?.displayCount}"`,
            f.properties?.geometryId,
        ]);

    return (
        // add the header
        headerRow.join(',') +
        '\n' +
        // add our data rows
        dataRows.map(row => row.join(',')).join('\n')
    );
};

/**
 * Prepares a GeoJSON data string for downloading Routes data. Handles
 * converting to JSON and prepending the data format type.
 */
const generateRoutesGeoJson = routes => {
    const { _data: data } = routes;

    if (data == null) {
        return;
    }

    let stringifiedData = '';
    // we expect that data will be a FeatureCollection all of the time, but handle
    // other formats just in case
    if (typeof data === 'string') {
        stringifiedData = data;
    } else if (data.type === 'Feature') {
        stringifiedData = JSON.stringify(data);
    } else {
        const modifiedData = data.features.map(feature => {
            return {
                ...feature,
                properties: {
                    name: feature.properties?.name,
                    geometryId: feature.properties?.geometryId,
                    percentage: feature.properties?.percentage,
                    displayCount: feature.properties?.displayCount,
                    // for segments with less than 100 trips, we don't know the exact count
                    // because of our privacy filtering. we'll still show those segments in
                    // the download, but we set the count field to null so that consumers of
                    // this data know it's not actually 100 exactly
                    count:
                        feature.properties?.displayCount === '< 100'
                            ? null
                            : feature.properties?.count,
                },
            };
        });

        stringifiedData = JSON.stringify({
            type: 'FeatureCollection',
            features: modifiedData,
        });
    }

    return stringifiedData;
};

interface DownloadDataViewProps {
    toggleModal: () => void;
    earliestAvailableMetrics: string;
    map: any;
    applyExternalTripCounts: boolean;
}

function DownloadDataView({
    toggleModal,
    earliestAvailableMetrics,
    map,
    applyExternalTripCounts,
}: DownloadDataViewProps) {
    const router = useRouter();
    const linearUnits = useLinearUnitsInfo();
    const slug = router.query['slug'] as string;
    const timeRange = router.query['time'] ?? 'all';
    const vehicleClass = router.query['vehicle'] ?? 'all';

    if (typeof timeRange !== 'string' || typeof vehicleClass !== 'string') {
        throw new Error(
            `timeRange and vehicleClass query params must be strings, got timeRange: ${timeRange} and vehicleClass: ${vehicleClass}`
        );
    }

    /**
     * onClick handler that generates data for the specified format,
     * downloads the data, then closes the modal.
     */
    const handleDownload =
        (
            dataFormat: 'geojson' | 'csv',
            dataType: 'routes' | 'metrics',
            applyExternalTripCounts?: boolean
        ) =>
        async () => {
            const displayVehicleClass =
                vehicleClass === 'all' ? 'all-vehicles' : vehicleClass;
            const displayTimeRange =
                timeRange === 'all' ? 'all-time' : timeRange;

            let externalTripCounts: ExternalTripCountsData | null = null;

            if (applyExternalTripCounts) {
                externalTripCounts = await api.getExternalTripCounts(slug);
            }
            // metrics data is for all time periods combined, routes has separate
            // files for each time period
            const filename =
                dataType === 'metrics'
                    ? `${slug}-${dataType}-data-for-${displayVehicleClass}.${dataFormat}`
                    : `${slug}-${dataType}-data-for-${displayVehicleClass}-in-${displayTimeRange}.${dataFormat}`;

            let data: string | undefined;

            if (dataType === 'metrics') {
                data = await generateMetricsCSV(
                    slug,
                    vehicleClass,
                    earliestAvailableMetrics,
                    linearUnits,
                    externalTripCounts
                );
            } else if (dataType === 'routes') {
                if (map == null) {
                    alert('No routes data to download.');
                    window.rollbar?.error('no routes data to download.');
                    return;
                }
                const routesDataSource = map.getSource('aggregated-routes');
                data =
                    dataFormat === 'geojson'
                        ? generateRoutesGeoJson(routesDataSource)
                        : generateRoutesCSV(routesDataSource);
            }

            if (data == null) {
                toggleModal();
                alert('No data to download.');
                return;
            }

            downloadData(filename, data);
            toggleModal();
        };

    return (
        <div className={styles.modal}>
            <h2 className={styles.subheader}>Download Data</h2>
            <p>Select the dataset and format to download.</p>
            <div className={styles.exports}>
                <div
                    className={styles.exportLink}
                    onClick={handleDownload(
                        'csv',
                        'metrics',
                        applyExternalTripCounts
                    )}
                >
                    <Icon icon="Spreadsheet" />
                    Export Metrics as CSV
                </div>
                <hr className={styles.spacer} />
                <div
                    className={styles.exportLink}
                    onClick={handleDownload('geojson', 'routes')}
                >
                    <Icon icon="List-File" />
                    Export Routes as GeoJSON
                </div>
                <div
                    className={styles.exportLink}
                    onClick={handleDownload('csv', 'routes')}
                >
                    <Icon icon="Spreadsheet" />
                    Export Routes as CSV
                </div>
            </div>
            <p>
                Data may be used with a link to public.ridereport.com and
                attribution to Ride Report.
            </p>
            <div className={styles.cancelContainer}>
                <button
                    className="button blue"
                    type="button"
                    onClick={toggleModal}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

interface DownloadDataEmailFormProps {
    toggleModal: () => void;
    setHasEnteredEmail: (hasEnteredEmail: boolean) => void;
}

function DownloadDataEmailForm({
    toggleModal,
    setHasEnteredEmail,
}: DownloadDataEmailFormProps) {
    const [hasForm, setHasForm] = useState(false);

    useEffect(() => {
        if (hasForm) return;

        // load the HubSpot script for forms
        // https://legacydocs.hubspot.com/docs/methods/forms/advanced_form_options
        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/v2.js';
        document.body.appendChild(script);

        const handleLoad = () => {
            if ((window as any).hbspt) {
                setHasForm(true);
                (window as any).hbspt.forms.create({
                    region: 'na1',
                    portalId: '5891093',
                    formId: '50ba03cd-bf2c-421d-a135-5b1defd014eb',
                    target: '#hubspot-form-50ba03cd-bf2c-421d-a135-5b1defd014eb',
                    onFormSubmitted: function () {
                        setHasEnteredEmail(true);
                    },
                });
                // lol this is absolutely ridiculous.
                // HubSpot requires jQuery to be present to use the onFormSubmit
                // callback for some unknown reason. We don't really want to load up
                // jQuery just to know that a form submitted, so we stub out jQuery
                // below and.....it all works?
                // https://www.unstack.com/blog/hubspot-on-form-submit-callbacks-without-jquery
                (window as any).jQuery =
                    (window as any).jQuery ||
                    function (nodeOrSelector) {
                        if (typeof nodeOrSelector == 'string') {
                            return document.querySelector(nodeOrSelector);
                        }
                        return nodeOrSelector;
                    };
            }
        };

        script.addEventListener('load', handleLoad);
        return () => {
            script.removeEventListener('load', handleLoad);
        };
    }, [hasForm, setHasForm, setHasEnteredEmail]);

    return (
        <div className={styles.hubspotFormContainer}>
            <div id="hubspot-form-50ba03cd-bf2c-421d-a135-5b1defd014eb" />
            <div className={styles.cancelContainer}>
                <button
                    className={styles.cancel}
                    type="button"
                    onClick={toggleModal}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

interface DownloadDataModalProps {
    earliestAvailableMetrics: string;
    map: any;
    applyExternalTripCounts: boolean;
}

function DownloadDataModal({
    earliestAvailableMetrics,
    map,
    applyExternalTripCounts,
}: DownloadDataModalProps) {
    const { isModalShowing, toggleModal } = useModal();
    const [hasEnteredEmail, setHasEnteredEmail] = useState(false);

    return (
        <>
            <div className={styles.button} onClick={toggleModal}>
                <Icon icon="Download" /> Download Data
            </div>

            <Modal isModalShowing={isModalShowing}>
                {!hasEnteredEmail ? (
                    <DownloadDataEmailForm
                        toggleModal={toggleModal}
                        setHasEnteredEmail={setHasEnteredEmail}
                    />
                ) : (
                    <DownloadDataView
                        toggleModal={toggleModal}
                        map={map}
                        earliestAvailableMetrics={earliestAvailableMetrics}
                        applyExternalTripCounts={applyExternalTripCounts}
                    />
                )}
            </Modal>
        </>
    );
}

export default DownloadDataModal;
